import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type BlueprintKind = 'house' | 'window' | 'door' | 'roof' | 'siding' | 'plan'

type Span = [number, number]

const TEXT = {
    fill: 'currentColor',
    stroke: 'none',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 12,
    letterSpacing: 3
}

const Tick: FC<{ x: number, y: number }> = ({ x, y }): ReactNode => (
    <line x1={x - 4} y1={y + 4} x2={x + 4} y2={y - 4} strokeWidth={2} />
)

const DimH: FC<{ x1: number, x2: number, y: number, from: number }> = ({ x1, x2, y, from }): ReactNode => (
    <g>
        <line x1={x1} y1={from} x2={x1} y2={y + 6} strokeDasharray='3 4' />
        <line x1={x2} y1={from} x2={x2} y2={y + 6} strokeDasharray='3 4' />
        <line x1={x1 - 8} y1={y} x2={x2 + 8} y2={y} />
        <Tick x={x1} y={y} />
        <Tick x={x2} y={y} />
    </g>
)

const DimV: FC<{ y1: number, y2: number, x: number, from: number }> = ({ y1, y2, x, from }): ReactNode => (
    <g>
        <line x1={from} y1={y1} x2={x - 6} y2={y1} strokeDasharray='3 4' />
        <line x1={from} y1={y2} x2={x - 6} y2={y2} strokeDasharray='3 4' />
        <line x1={x} y1={y1 - 8} x2={x} y2={y2 + 8} />
        <Tick x={x} y={y1} />
        <Tick x={x} y={y2} />
    </g>
)

// Cuts the openings out of one row of lap siding.
const subtract = (from: number, to: number, holes: Span[]): Span[] => holes.reduce<Span[]>(
    (segments, [a, b]) => segments.flatMap(([start, end]): Span[] => {
        if (b <= start || a >= end) return [[start, end]]

        return [
            ...(a > start ? [[start, a] as Span] : []),
            ...(b < end ? [[b, end] as Span] : [])
        ]
    }),
    [[from, to]]
)

const HOUSE_OPENINGS = [
    { x: [154, 236] as Span, y: [222, 324] as Span },
    { x: [364, 446] as Span, y: [222, 324] as Span },
    { x: [268, 332] as Span, y: [258, 360] as Span }
]

const SIDING = Array.from({ length: 12 }, (_, index) => 218 + index * 12).flatMap(y => subtract(
    130,
    470,
    HOUSE_OPENINGS.filter(opening => y >= opening.y[0] && y <= opening.y[1]).map(opening => opening.x)
).map(([x1, x2]) => ({ y, x1, x2 })))

const HouseWindow: FC<{ x: number, y: number }> = ({ x, y }): ReactNode => (
    <g>
        <rect x={x - 6} y={y - 6} width={82} height={96} />
        <rect x={x} y={y} width={70} height={84} />
        <line x1={x} y1={y + 42} x2={x + 70} y2={y + 42} />
        <line x1={x + 35} y1={y} x2={x + 35} y2={y + 84} />
        <polygon points={`${x - 10},${y + 90} ${x + 80},${y + 90} ${x + 82},${y + 96} ${x - 12},${y + 96}`} />
    </g>
)

const House: FC = (): ReactNode => (
    <>
        <line x1={40} y1={360} x2={560} y2={360} strokeWidth={2} />
        {Array.from({ length: 33 }, (_, index) => 48 + index * 16).map(x => (
            <line key={x} x1={x} y1={360} x2={x - 8} y2={368} opacity={0.5} />
        ))}

        <rect x={130} y={206} width={340} height={154} />
        <polyline points='100,206 300,86 500,206' strokeWidth={2} />
        <polyline points='116,206 300,96 484,206' />
        <line x1={100} y1={206} x2={500} y2={206} />

        <polyline points='392,141 392,104 420,104 420,158' />
        <rect x={388} y={98} width={36} height={6} />

        <circle cx={300} cy={150} r={15} />
        <line x1={290} y1={144} x2={310} y2={144} />
        <line x1={287} y1={150} x2={313} y2={150} />
        <line x1={290} y1={156} x2={310} y2={156} />

        <g opacity={0.45}>
            {SIDING.map(({ y, x1, x2 }) => <line key={`${y}-${x1}`} x1={x1} y1={y} x2={x2} y2={y} />)}
        </g>

        <HouseWindow x={160} y={228} />
        <HouseWindow x={370} y={228} />

        <rect x={268} y={258} width={64} height={102} />
        <rect x={276} y={266} width={48} height={94} />
        <rect x={284} y={276} width={32} height={30} />
        <rect x={284} y={316} width={13} height={36} />
        <rect x={303} y={316} width={13} height={36} />
        <circle cx={318} cy={312} r={2.5} />

        <DimH x1={130} x2={470} y={392} from={366} />
        <DimV y1={86} y2={360} x={530} from={310} />

        <text x={40} y={424} {...TEXT}>FRONT ELEVATION</text>
        <text x={560} y={424} textAnchor='end' {...TEXT}>N.T.S.</text>
    </>
)

const Window: FC = (): ReactNode => (
    <>
        <rect x={80} y={40} width={220} height={320} />
        <rect x={96} y={56} width={188} height={288} />

        <rect x={108} y={68} width={164} height={132} />
        <rect x={118} y={78} width={144} height={112} />
        <line x1={190} y1={78} x2={190} y2={190} />
        <line x1={118} y1={134} x2={262} y2={134} />

        <line x1={96} y1={204} x2={284} y2={204} />
        <line x1={96} y1={210} x2={284} y2={210} />

        <rect x={108} y={214} width={164} height={122} />
        <rect x={118} y={224} width={144} height={102} />
        <line x1={190} y1={224} x2={190} y2={326} />
        <line x1={118} y1={275} x2={262} y2={275} />

        <polygon points='70,360 310,360 318,374 62,374' />

        <line x1={330} y1={150} x2={330} y2={290} strokeDasharray='5 5' />
        <polyline points='324,160 330,150 336,160' />
        <polyline points='324,280 330,290 336,280' />

        <DimH x1={80} x2={300} y={398} from={380} />
        <DimV y1={40} y2={360} x={46} from={74} />

        <line x1={440} y1={40} x2={440} y2={118} />
        <line x1={500} y1={40} x2={500} y2={100} />
        <line x1={440} y1={340} x2={440} y2={400} />
        <line x1={500} y1={340} x2={500} y2={400} />
        {[44, 56, 68, 80, 92, 104, 344, 356, 368, 380].map(y => (
            <line key={y} x1={440} y1={y} x2={428} y2={y + 10} opacity={0.6} />
        ))}

        <rect x={440} y={100} width={60} height={24} />
        <line x1={440} y1={100} x2={500} y2={124} />
        <line x1={500} y1={100} x2={440} y2={124} />

        <rect x={446} y={124} width={48} height={202} />
        <line x1={464} y1={136} x2={464} y2={314} />
        <line x1={472} y1={136} x2={472} y2={314} />
        <line x1={446} y1={225} x2={494} y2={225} />

        <polyline points='494,326 446,326 420,336 420,342 446,334' />
        <rect x={440} y={326} width={60} height={14} />
        <line x1={440} y1={326} x2={500} y2={340} />
        <line x1={500} y1={326} x2={440} y2={340} />
        <polyline points='418,112 440,118 446,124' />

        <text x={40} y={424} {...TEXT}>WINDOW ELEVATION</text>
        <text x={420} y={424} {...TEXT}>SECTION A</text>
    </>
)

const Door: FC = (): ReactNode => (
    <>
        <rect x={160} y={46} width={206} height={324} />
        <rect x={170} y={56} width={186} height={314} />

        <rect x={178} y={64} width={110} height={306} />
        <rect x={194} y={82} width={78} height={104} />
        <line x1={233} y1={82} x2={233} y2={186} />
        <line x1={194} y1={134} x2={272} y2={134} />
        <rect x={194} y={206} width={32} height={146} />
        <rect x={200} y={212} width={20} height={134} />
        <rect x={240} y={206} width={32} height={146} />
        <rect x={246} y={212} width={20} height={134} />
        <circle cx={280} cy={250} r={5} />
        <circle cx={280} cy={230} r={3} />
        <rect x={178} y={90} width={4} height={14} />
        <rect x={178} y={210} width={4} height={14} />
        <rect x={178} y={320} width={4} height={14} />

        <rect x={288} y={56} width={10} height={314} />
        <rect x={304} y={64} width={44} height={306} />
        <rect x={312} y={74} width={28} height={286} />
        <line x1={312} y1={146} x2={340} y2={146} />
        <line x1={312} y1={218} x2={340} y2={218} />
        <line x1={312} y1={290} x2={340} y2={290} />

        <line x1={150} y1={370} x2={376} y2={370} strokeWidth={2} />
        <rect x={140} y={370} width={246} height={14} />

        <DimH x1={160} x2={366} y={404} from={390} />
        <DimV y1={46} y2={370} x={122} from={154} />

        <line x1={420} y1={280} x2={450} y2={280} />
        <line x1={420} y1={296} x2={450} y2={296} />
        <line x1={450} y1={280} x2={450} y2={296} />
        <line x1={530} y1={280} x2={580} y2={280} />
        <line x1={530} y1={296} x2={580} y2={296} />
        <line x1={530} y1={280} x2={530} y2={296} />
        <rect x={450} y={296} width={5} height={80} />
        <path d='M 530 296 A 80 80 0 0 1 450 376' strokeDasharray='4 5' />

        <text x={110} y={424} {...TEXT}>ENTRY DOOR ELEVATION</text>
        <text x={440} y={424} {...TEXT}>PLAN</text>
    </>
)

const Roof: FC = (): ReactNode => (
    <>
        <polyline points='60,300 300,100 540,300' strokeWidth={2} />
        <line x1={60} y1={300} x2={540} y2={300} />
        <line x1={140} y1={233} x2={220} y2={300} />
        <line x1={220} y1={300} x2={300} y2={100} />
        <line x1={300} y1={100} x2={380} y2={300} />
        <line x1={380} y1={300} x2={460} y2={233} />
        <polyline points='48,292 300,82 552,292' />
        <polyline points='40,286 300,72 560,286' strokeDasharray='10 6' />
        <polyline points='282,94 300,84 318,94' />

        <line x1={60} y1={300} x2={60} y2={380} />
        <line x1={80} y1={300} x2={80} y2={380} />
        <line x1={520} y1={300} x2={520} y2={380} />
        <line x1={540} y1={300} x2={540} y2={380} />
        <path d='M 28 290 L 28 306 Q 28 312 34 312 L 42 312 Q 48 312 48 306 L 48 292' />

        <line x1={420} y1={200} x2={480} y2={200} />
        <line x1={480} y1={200} x2={480} y2={250} />

        <DimH x1={60} x2={540} y={404} from={386} />
        <DimV y1={100} y2={300} x={578} from={310} />

        <text x={40} y={424} {...TEXT}>ROOF SECTION</text>
        <text x={560} y={424} textAnchor='end' {...TEXT}>TRUSS DETAIL</text>
    </>
)

const COURSES = Array.from({ length: 11 }, (_, index) => 40 + index * 30)

const Siding: FC = (): ReactNode => (
    <>
        <g opacity={0.6}>
            {COURSES.map(y => <line key={y} x1={60} y1={y + 30} x2={270} y2={y + 30} />)}
        </g>
        <rect x={110} y={130} width={100} height={130} />
        <rect x={120} y={140} width={80} height={110} />
        <line x1={120} y1={195} x2={200} y2={195} />

        <polyline points={COURSES.map(y => `316,${y} 294,${y + 28} 316,${y + 30}`).join(' ')} />
        <line x1={322} y1={40} x2={322} y2={370} strokeDasharray='6 5' />
        <rect x={326} y={40} width={14} height={330} />
        <line x1={340} y1={40} x2={340} y2={370} />
        <line x1={420} y1={40} x2={420} y2={370} />
        <path d={`M 344 50 ${Array.from({ length: 16 }, (_, index) => `L ${index % 2 ? 344 : 416} ${50 + (index + 1) * 20}`).join(' ')}`} opacity={0.6} />
        <line x1={428} y1={40} x2={428} y2={370} />
        <rect x={290} y={370} width={150} height={34} />
        <line x1={290} y1={370} x2={440} y2={404} />
        <line x1={440} y1={370} x2={290} y2={404} />
        <polyline points='316,370 286,382 286,388' />

        <circle cx={304} cy={110} r={4} />
        <line x1={308} y1={110} x2={470} y2={110} />
        <circle cx={322} cy={170} r={4} />
        <line x1={326} y1={170} x2={470} y2={170} />
        <circle cx={333} cy={230} r={4} />
        <line x1={337} y1={230} x2={470} y2={230} />
        <text x={478} y={114} {...TEXT} fontSize={11} letterSpacing={2}>LAP SIDING</text>
        <text x={478} y={174} {...TEXT} fontSize={11} letterSpacing={2}>HOUSE WRAP</text>
        <text x={478} y={234} {...TEXT} fontSize={11} letterSpacing={2}>SHEATHING</text>

        <text x={60} y={424} {...TEXT}>SIDING ELEVATION</text>
        <text x={290} y={424} {...TEXT}>WALL SECTION</text>
    </>
)

const Plan: FC = (): ReactNode => (
    <>
        <polyline points='260,380 60,380 60,40 540,40 540,380 320,380' strokeWidth={2} />
        <polyline points='260,368 72,368 72,52 528,52 528,368 320,368' />
        <line x1={260} y1={368} x2={260} y2={380} />
        <line x1={320} y1={368} x2={320} y2={380} />
        <line x1={260} y1={368} x2={260} y2={308} />
        <path d='M 320 368 A 60 60 0 0 0 260 308' strokeDasharray='4 5' />

        <line x1={300} y1={52} x2={300} y2={200} strokeWidth={3} />
        <line x1={72} y1={220} x2={200} y2={220} strokeWidth={3} />
        <line x1={260} y1={220} x2={300} y2={220} strokeWidth={3} />
        <line x1={200} y1={220} x2={200} y2={168} />
        <path d='M 252 220 A 52 52 0 0 0 200 168' strokeDasharray='4 5' />
        <line x1={420} y1={52} x2={420} y2={240} strokeWidth={3} />
        <line x1={420} y1={290} x2={420} y2={368} strokeWidth={3} />

        <rect x={120} y={40} width={80} height={12} />
        <line x1={120} y1={46} x2={200} y2={46} />
        <rect x={340} y={40} width={60} height={12} />
        <line x1={340} y1={46} x2={400} y2={46} />
        <rect x={440} y={40} width={70} height={12} />
        <line x1={440} y1={46} x2={510} y2={46} />
        <rect x={60} y={260} width={12} height={70} />
        <line x1={66} y1={260} x2={66} y2={330} />
        <rect x={528} y={110} width={12} height={80} />
        <line x1={534} y1={110} x2={534} y2={190} />
        <rect x={100} y={368} width={90} height={12} />
        <line x1={100} y1={374} x2={190} y2={374} />

        <DimH x1={60} x2={540} y={404} from={386} />
        <DimV y1={40} y2={380} x={30} from={54} />

        <text x={560} y={424} textAnchor='end' {...TEXT}>FLOOR PLAN</text>
    </>
)

const DRAWINGS: Record<BlueprintKind, FC> = {
    house: House,
    window: Window,
    door: Door,
    roof: Roof,
    siding: Siding,
    plan: Plan
}

interface BlueprintProps {
    kind: BlueprintKind
    className?: string
    side?: 'left' | 'right'
    tone?: 'light' | 'dark'
}

// Faint architectural line drawing on a drafting grid, used as a background in
// the empty parts of page openings and section headers. Purely decorative, so
// it is hidden from screen readers. The caller positions and sizes the box (the
// drawing fills it), fades it with a mask class, and keeps it behind the content
// (`-z-10` inside an `isolate` section). `tone='dark'` draws white lines for navy bands.
const Blueprint: FC<BlueprintProps> = ({ kind, className, side = 'right', tone = 'light' }): ReactNode => {
    const Drawing = DRAWINGS[kind]
    const dark = tone === 'dark'

    return (
        <div aria-hidden='true' className={cn('pointer-events-none select-none', dark ? 'text-white' : 'text-[#0b4f8f]', className)}>
            <div className={cn('absolute inset-0 bg-[linear-gradient(currentColor_1px,transparent_1px),linear-gradient(90deg,currentColor_1px,transparent_1px)] bg-[size:28px_28px]', dark ? 'opacity-[0.07]' : 'opacity-[0.06]')} />

            <svg
                viewBox='0 0 600 430'
                className={cn('absolute top-1/2 w-[min(40rem,88%)] -translate-y-1/2', side === 'right' ? 'right-[6%]' : 'left-[6%]', dark ? 'opacity-[0.16]' : 'opacity-[0.2]')}
                fill='none'
                stroke='currentColor'
                strokeWidth={1.5}
                strokeLinecap='round'
                strokeLinejoin='round'
            >
                <Drawing />
            </svg>
        </div>
    )
}

export default Blueprint

// Box for a section header: the top right of a section, faded out on every side.
export const BLUEPRINT_CORNER = 'absolute right-0 top-0 -z-10 hidden h-[26rem] w-[50%] [mask-image:radial-gradient(ellipse_at_62%_42%,#000_25%,transparent_70%)] lg:block'
